"use client"

import { useState, useRef, useEffect } from "react"
import styled from "styled-components"
import { Send, X, Settings, User } from "react-feather"
import Card from "../ui/Card"

const ChatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid ${(props) => props.theme.border};
`

const ChatTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
`

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: none;
  background-color: transparent;
  color: ${(props) => props.theme.text};
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => props.theme.accent};
  }
`

const ChatContent = styled.div`
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SenderName = styled.span`
  font-size: 14px;
  font-weight: 500;
`

const MessageTime = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryText};
`

const MessageText = styled.p`
  font-size: 14px;
`

const ChatFooter = styled.div`
  padding: 12px;
  border-top: 1px solid ${(props) => props.theme.border};
`

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChatInput = styled.input`
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.border};
  background-color: ${(props) => props.theme.input};
  color: ${(props) => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
`

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: none;
  background-color: ${(props) => props.theme.primary};
  color: white;
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => props.theme.primaryLight};
  }
`

const MinimizedButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.border};
  background-color: ${(props) => props.theme.background};
  color: ${(props) => props.theme.text};
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => props.theme.accent};
  }
`

function ChatWidget() {
  const [messages, setMessages] = useState([
    {
      id: "1",
      sender: "System",
      content: "Welcome to the crypto trading chat! Ask questions or share insights with the community.",
      timestamp: new Date(),
    },
  ])

  const [input, setInput] = useState("")
  const [username, setUsername] = useState("User" + Math.floor(Math.random() * 1000))
  const [isMinimized, setIsMinimized] = useState(false)

  const messagesEndRef = useRef(null)

  // Auto-scroll to bottom when new messages come in
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  const handleSendMessage = () => {
    if (input.trim() === "") return

    const newMessage = {
      id: Date.now().toString(),
      sender: username,
      content: input.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, newMessage])
    setInput("")

    // Simulate response after a short delay
    if (Math.random() > 0.7) {
      setTimeout(
        () => {
          const responses = [
            "BTC looking bullish today!",
            "What do you think about the ETH price action?",
            "Anyone watching the funding rates?",
            "Kimchi premium is increasing, interesting...",
            "Long/short ratio is favoring longs right now.",
          ]

          const botNames = ["Trader123", "CryptoWhale", "Satoshi2023", "BlockchainExpert"]

          const botMessage = {
            id: Date.now().toString(),
            sender: botNames[Math.floor(Math.random() * botNames.length)],
            content: responses[Math.floor(Math.random() * responses.length)],
            timestamp: new Date(),
          }

          setMessages((prev) => [...prev, botMessage])
        },
        1000 + Math.random() * 2000,
      )
    }
  }

  if (isMinimized) {
    return (
      <MinimizedButton onClick={() => setIsMinimized(false)}>
        <User size={16} />
        Open Chat
      </MinimizedButton>
    )
  }

  return (
    <ChatCard>
      <ChatHeader>
        <ChatTitle>Community Chat</ChatTitle>
        <HeaderButtons>
          <IconButton>
            <Settings size={16} />
          </IconButton>
          <IconButton onClick={() => setIsMinimized(true)}>
            <X size={16} />
          </IconButton>
        </HeaderButtons>
      </ChatHeader>
      <ChatContent>
        {messages.map((message) => (
          <MessageContainer key={message.id}>
            <MessageHeader>
              <SenderName>{message.sender}</SenderName>
              <MessageTime>
                {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </MessageTime>
            </MessageHeader>
            <MessageText>{message.content}</MessageText>
          </MessageContainer>
        ))}
        <div ref={messagesEndRef} />
      </ChatContent>
      <ChatFooter>
        <InputContainer>
          <ChatInput
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSendMessage()
              }
            }}
          />
          <SendButton onClick={handleSendMessage}>
            <Send size={16} />
          </SendButton>
        </InputContainer>
      </ChatFooter>
    </ChatCard>
  )
}

export default ChatWidget

